export const productsData = {
  udc: {
    slug: 'udc',
    title: 'Unified Digital Campus',
    shortTitle: 'UDC',
    tagline: 'One ecosystem. Every function. Zero silos.',
    category: 'Platform',
    description:
      'The architectural backbone of a future-ready institution  UDC unifies HRMS, Payroll, Accounts, LMS, and every administrative module into a single synchronized digital ecosystem.',
    longDescription:
      "A modern institution does not run on isolated systems  it runs on synchronization. Euclideum's Unified Digital Campus (UDC) is designed as the central digital roof that brings all institutional operations under one intelligent, connected ecosystem.",
    images: [
      '/products/campus.webp',
      '/products/campus3.webp',
      '/products/campus2.webp',
      '/products/campus4.webp',
    ],
    stats: [
      { value: '100%', label: 'Module Integration' },
      { value: '6x', label: 'Faster Decision Cycles' },
      { value: '85%', label: 'Reduction in Data Silos' },
      { value: '99.9%', label: 'System Uptime' },
    ],
    features: [
      {
        icon: 'Globe',
        title: 'End-to-End Student Lifecycle',
        description:
          'From admissions to graduation, every touchpoint managed within one cohesive system with zero data loss.',
        stat: 'Zero data loss',
      },
      {
        icon: 'Zap',
        title: 'Cross-Module Data Flow',
        description:
          'LMS, HRMS, Payroll, and Accounts share live data  no duplication, no lag, no reconciliation headaches.',
        stat: '10x faster sync',
      },
      {
        icon: 'Shield',
        title: 'Role-Based Access Control',
        description:
          'Granular permissions ensure every user sees exactly what they need  no more, no less.',
        stat: 'Zero over-exposure',
      },
      {
        icon: 'BarChart3',
        title: 'Leadership Dashboards',
        description:
          'Live dashboard for institutional KPIs, operational health, and compliance',
        stat: 'Live, always',
      },
    ],
    modules: [
      {
        name: 'HRMS',
        icon: 'Users',
        description: 'Complete employee lifecycle management',
      },
      {
        name: 'Payroll',
        icon: 'DollarSign',
        description: 'Automated salary and compliance',
      },
      {
        name: 'Accounts',
        icon: 'BarChart3',
        description: 'Real-time financial management',
      },
      {
        name: 'LMS',
        icon: 'BookOpen',
        description: 'AI-powered learning delivery',
      },
      {
        name: 'Cloud',
        icon: 'Cloud',
        description: 'Enterprise-grade infrastructure',
      },
      {
        name: 'AI Engine',
        icon: 'Brain',
        description: 'Intelligent insights and automation',
      },
    ],
    benefits: [
      {
        title: 'Eliminate Data Silos',
        description:
          'Break down barriers between departments with unified data flow and real-time synchronization across every module.',
        icon: 'Layers',
      },
      {
        title: 'Accelerate Decision Making',
        description:
          'Leadership gains real-time visibility into institutional performance with centralized, drill-down dashboards.',
        icon: 'Gauge',
      },
      {
        title: 'Enhance Security & Compliance',
        description:
          'Role-based access controls and comprehensive audit trails ensure data protection and regulatory compliance.',
        icon: 'Fingerprint',
      },
      {
        title: 'Scale with Confidence',
        description:
          'Cloud-native architecture that grows with your institution, handling peak loads without performance degradation.',
        icon: 'Rocket',
      },
    ],
    capabilities: [
      'End-to-end student lifecycle management',
      'Integrated LMS for digital academic delivery',
      'HRMS for faculty and staff management',
      'Automated payroll and compliance engine',
      'Accounts and financial governance module',
      'Secure, role-based access control',
      'Real-time analytics and leadership dashboards',
      'Mobile-first responsive user experience',
    ],
    quote: {
      text: 'The transition felt effortless. What changed was not just our system it was the way our academic ecosystem started functioning with clarity and confidence.',
      author: 'Director, DY Patil College',
      role: 'Engineering & Technology',
    },
  },
  lms: {
    slug: 'lms',
    title: 'Learning Management System',
    shortTitle: 'LMS',
    tagline: 'Learning that adapts. Outcomes that compound.',
    category: 'Module',
    description:
      'Deliver, manage, and track educational programs with precision and scalability. The LMS centralizes curriculum management, content distribution, assessments, and analytics within a secure digital ecosystem.',
    longDescription:
      "In a digital-first world, learning must be structured, measurable, and genuinely engaging. Euclideum's LMS provides a comprehensive platform for delivering, managing, and tracking educational programs with precision and scalability.",
    images: [
      '/products/lms1.webp',
      '/products/lms2.webp',
      '/products/lms3.webp',
      '/products/lms4.webp',
    ],
    stats: [
      { value: '75%', label: 'Higher Completion Rates' },
      { value: '90%', label: 'Boost in Engagement' },
      { value: '60%', label: 'Lower Training Costs' },
      { value: '100%', label: 'Compliance Achieved' },
    ],
    features: [
      {
        icon: 'Brain',
        title: 'AI-Adaptive Learning Paths',
        description:
          "Content, pace, and difficulty adjust in real-time based on each learner's individual performance patterns.",
        stat: '40% better retention',
      },
      {
        icon: 'BookOpen',
        title: 'Course & Curriculum Builder',
        description:
          'Drag-and-drop authoring with video, assessments, SCORM, and interactive content support.',
        stat: 'Built in minutes',
      },
      {
        icon: 'Target',
        title: 'Progress & Completion Tracking',
        description:
          'Real-time visibility into learner journeys, completion rates, and granular skill gap analysis.',
        stat: 'Per-learner insight',
      },
      {
        icon: 'Award',
        title: 'Compliance & Certification',
        description:
          'Built in Automated recertification reminders, audit trails, and 100% compliance tracking.',
        stat: 'Never miss a deadline',
      },
    ],
    modules: [
      {
        name: 'Course Management',
        icon: 'BookOpen',
        description: 'Curriculum and content creation',
      },
      {
        name: 'Assessments',
        icon: 'Target',
        description: 'Automated grading and feedback',
      },
      {
        name: 'Analytics',
        icon: 'BarChart3',
        description: 'Learner progress tracking',
      },
      {
        name: 'Certification',
        icon: 'Award',
        description: 'Automated certification engine',
      },
      {
        name: 'Mobile Learning',
        icon: 'Globe',
        description: 'Learn on any device',
      },
      {
        name: 'AI Personalization',
        icon: 'Brain',
        description: 'Adaptive learning paths',
      },
    ],
    benefits: [
      {
        title: 'Personalize Every Learning Journey',
        description:
          "AI-driven adaptive paths that cater to each individual learner's needs, pace, and performance level.",
        icon: 'Brain',
      },
      {
        title: 'Increase Measurable Engagement',
        description:
          'Interactive content, gamification elements, and mobile access that keep learners genuinely engaged.',
        icon: 'Users',
      },
      {
        title: 'Track Progress with Precision',
        description:
          'Real-time visibility into learner performance, completion rates, and institutional learning outcomes.',
        icon: 'BarChart3',
      },
      {
        title: 'Ensure Full Compliance',
        description:
          'Automated tracking and proactive reminders for certification, recertification, and compliance training.',
        icon: 'Shield',
      },
    ],
    capabilities: [
      'Course and curriculum management',
      'Assignment submission and auto-grading',
      'Attendance and progress tracking',
      'Communication tools for active engagement',
      'Performance analytics dashboards',
      'Secure and scalable cloud infrastructure',
      'Mobile-responsive design for all devices',
      'SCORM and xAPI compatibility',
    ],
    quote: {
      text: "Euclideum's solution provided the structure we needed to scale efficiently. It strengthened transparency and streamlined our academic processes significantly.",
      author: 'Academic Director, NMIMS University',
      role: 'Higher Education',
    },
  },
  hrms: {
    slug: 'hrms',
    title: 'Human Resource Management System',
    shortTitle: 'HRMS',
    tagline: 'From onboarding to excellence automated.',
    category: 'Module',
    description:
      'Streamline and centralize the entire employee lifecycle into one intelligent platform. HRMS transforms manual HR tasks into automated workflows, giving leadership structured insights.',
    longDescription:
      "Managing people effectively is essential to building a high-performing organization. Euclideum's HRMS is designed to streamline and centralize the entire employee lifecycle from recruitment and onboarding to performance evaluation and compliance.",
    images: [
      '/products/hrms2.webp',
      '/products/hrms1.webp',
      '/products/hrms3.webp',
      '/products/hrms4.webp',
    ],
    stats: [
      { value: '70%', label: 'Less Admin Time' },
      { value: '45%', label: 'Higher Engagement' },
      { value: '80%', label: 'Faster Onboarding' },
      { value: '90%', label: 'Compliance Coverage' },
    ],
    features: [
      {
        icon: 'Users',
        title: 'Centralized Employee Profiles',
        description:
          'All employee data  documents, history, roles  in one secure, searchable, and fully auditable record.',
        stat: 'Single source of truth',
      },
      {
        icon: 'Zap',
        title: 'Automated Onboarding',
        description:
          'From offer letter to first-day checklist, every step is triggered and tracked automatically.',
        stat: '3 days → 4 hours',
      },
      {
        icon: 'Clock',
        title: 'Leave & Attendance Engine',
        description:
          'Real-time tracking with shift scheduling, biometric integration, and policy enforcement.',
        stat: 'Zero manual entry',
      },
      {
        icon: 'Target',
        title: 'Performance Appraisal',
        description:
          '360° reviews, OKR tracking, and continuous feedback loops that genuinely drive growth.',
        stat: '45% productivity uplift',
      },
    ],
    modules: [
      {
        name: 'Recruitment',
        icon: 'Users',
        description: 'End-to-end talent acquisition',
      },
      {
        name: 'Onboarding',
        icon: 'Rocket',
        description: 'Automated employee integration',
      },
      {
        name: 'Attendance',
        icon: 'Clock',
        description: 'Real-time time tracking',
      },
      {
        name: 'Performance',
        icon: 'Target',
        description: 'Goal setting and reviews',
      },
      {
        name: 'Compliance',
        icon: 'Shield',
        description: 'Policy and regulatory tracking',
      },
      {
        name: 'Analytics',
        icon: 'BarChart3',
        description: 'Workforce insights and reporting',
      },
    ],
    benefits: [
      {
        title: 'Reduce Administrative Burden',
        description:
          'Automate repetitive HR tasks and free your team to focus on what matters  your people.',
        icon: 'Zap',
      },
      {
        title: 'Improve Employee Experience',
        description:
          'Self-service portals and transparent processes that genuinely empower employees at every level.',
        icon: 'HeartHandshake',
      },
      {
        title: 'Ensure Compliance',
        description:
          'Stay compliant with labor laws and regulations with automated policy enforcement and audit trails.',
        icon: 'Shield',
      },
      {
        title: 'Drive Performance',
        description:
          'Continuous feedback and OKR tracking that align individual growth with organizational goals.',
        icon: 'TrendingUp',
      },
    ],
    capabilities: [
      'Centralized employee information management',
      'Recruitment and onboarding automation',
      'Attendance, leave, and shift scheduling',
      'Performance appraisal and OKR tracking',
      'Policy documentation and compliance monitoring',
      'Real-time analytics and HR dashboards',
      'Employee self-service portal',
      'Mobile access for all staff',
    ],
    quote: {
      text: 'The system brought better structure and efficiency to HR operations.',
      author: 'HR Director, JSKB Associates',
      role: 'Financial Services',
    },
  },
  payroll: {
    slug: 'payroll',
    title: 'Payroll Management System',
    shortTitle: 'Payroll',
    tagline: 'Precision payroll. Zero pressure.',
    category: 'Module',
    description:
      'Automate complex salary structures, statutory requirements, and tax calculations for seamless, error-free payroll cycles  integrated with HR and attendance to eliminate duplication.',
    longDescription:
      "Payroll demands accuracy, compliance, and timeliness  every single cycle. Euclideum's Payroll Management System automates complex salary structures and statutory requirements to ensure seamless, error-free payroll processing.",
    images: [
      '/products/payroll1.webp',
      '/products/payroll4.webp',
      '/products/payroll2.webp',
      '/products/payroll3.webp',
    ],
    stats: [
      { value: '100%', label: 'Error Elimination' },
      { value: '40hrs', label: 'Saved Per Cycle' },
      { value: '65%', label: 'Faster Processing' },
      { value: '50%', label: 'Cost Reduction' },
    ],
    features: [
      {
        icon: 'DollarSign',
        title: 'Automated Calculations',
        description:
          'Salary, deductions, overtime, bonuses  all computed accurately with zero manual input required.',
        stat: 'Zero rounding errors',
      },
      {
        icon: 'Shield',
        title: 'Tax & Statutory Compliance',
        description:
          'Always current with regulatory changes  built-in TDS, PF, ESI, and GST handling.',
        stat: 'Auto-updated rules',
      },
      {
        icon: 'FileText',
        title: 'Digital Payslip Engine',
        description:
          'Branded, detailed payslips generated and distributed automatically every single cycle.',
        stat: 'Delivered in seconds',
      },
      {
        icon: 'BarChart3',
        title: 'Audit-Ready Reports',
        description:
          'Complete payroll history, statutory filings, and bank-reconciled records always at your fingertips.',
        stat: '12-month trail',
      },
    ],
    modules: [
      {
        name: 'Salary Processing',
        icon: 'DollarSign',
        description: 'Automated salary computation',
      },
      {
        name: 'Tax Management',
        icon: 'Shield',
        description: 'TDS, PF, ESI calculations',
      },
      {
        name: 'Compliance',
        icon: 'CheckCircle2',
        description: 'Statutory compliance automation',
      },
      {
        name: 'Payslips',
        icon: 'FileText',
        description: 'Digital payslip generation',
      },
      {
        name: 'Reporting',
        icon: 'BarChart3',
        description: 'Audit-ready reports',
      },
      {
        name: 'Integrations',
        icon: 'Settings',
        description: 'Bank and HRMS integration',
      },
    ],
    benefits: [
      {
        title: 'Eliminate Errors Completely',
        description:
          'Automated calculations ensure 100% accuracy in salary processing and statutory deductions every cycle.',
        icon: 'CheckCircle2',
      },
      {
        title: 'Save Significant Time',
        description:
          'Reduce payroll processing time from days to hours with intelligent, end-to-end automation.',
        icon: 'Clock',
      },
      {
        title: 'Ensure Compliance',
        description:
          'Stay compliant with changing tax laws and statutory requirements without manual intervention.',
        icon: 'Shield',
      },
      {
        title: 'Enhance Employee Trust',
        description:
          'Transparent payroll processing and self-service access to payslips and tax documents, anytime.',
        icon: 'Users',
      },
    ],
    capabilities: [
      'Automated salary and deduction calculations',
      'Tax and statutory compliance management',
      'Overtime, incentives, and bonus processing',
      'Digital payslip generation and distribution',
      'Bank transfer integration and reconciliation',
      'Audit-ready payroll reports and filings',
      'Employee self-service payroll portal',
      'Multi-entity payroll support',
    ],
    quote: {
      text: 'The system improved structure, control, and efficiency in our financial operations.',
      author: 'Finance Head, JSKB Associates',
      role: 'Financial Services',
    },
  },
  accounts: {
    slug: 'accounts',
    title: 'Accounting Management System',
    shortTitle: 'Accounts',
    tagline: 'Financial clarity. Strategic confidence.',
    category: 'Module',
    description:
      'Modernize accounting operations with automated workflows, centralized financial data, and real-time reporting. From ledger management to GST compliance  accuracy auditability, and proactive planning.',
    longDescription:
      "Financial control is the foundation of sustainable growth. Euclideum's Accounts Management System modernizes accounting by automating workflows, centralizing financial data, and delivering real-time reporting  replacing fragmented tools and manual reconciliations.",
    images: [
      '/products/account1.webp',
      '/products/account4.webp',
      '/products/account3.webp',
      '/products/account2.webp',
    ],
    stats: [
      { value: '80%', label: 'Faster Month-End Close' },
      { value: '99.9%', label: 'Accuracy Achieved' },
      { value: '70%', label: 'Less Reconciliation Time' },
      { value: '50%', label: 'Faster Decisions' },
    ],
    features: [
      {
        icon: 'BookOpen',
        title: 'Automated Ledger Management',
        description:
          'Automate month-end close in days with automated journal entries, AP/AR, and reconciliation.',
        stat: 'Close in 2 days',
      },
      {
        icon: 'Shield',
        title: 'GST & Compliance Modules',
        description:
          'Built-in compliance with Indian and international accounting standards and tax regulations.',
        stat: 'Audit-ready always',
      },
      {
        icon: 'RefreshCw',
        title: 'Bank Reconciliation Engine',
        description:
          'Direct bank feed integration with intelligent transaction matching and real-time exception alerts.',
        stat: 'Matches in real-time',
      },
      {
        icon: 'Target',
        title: 'Budget & Expense Controls',
        description:
          'Set budgets, track actuals in real-time, and receive alerts before overspending occurs.',
        stat: 'Overspend prevented',
      },
    ],
    modules: [
      {
        name: 'Ledger',
        icon: 'BookOpen',
        description: 'Automated journal entries',
      },
      {
        name: 'AP/AR',
        icon: 'DollarSign',
        description: 'Payables and receivables',
      },
      {
        name: 'Reconciliation',
        icon: 'RefreshCw',
        description: 'Bank reconciliation automation',
      },
      { name: 'GST', icon: 'Shield', description: 'Tax compliance management' },
      {
        name: 'Budgeting',
        icon: 'Target',
        description: 'Budget planning and tracking',
      },
      {
        name: 'Reporting',
        icon: 'BarChart3',
        description: 'Real-time financial reports',
      },
    ],
    benefits: [
      {
        title: 'Accelerate Month-End Close',
        description:
          'Reduce close time from weeks to days with automated reconciliation and intelligent journal entries.',
        icon: 'Clock',
      },
      {
        title: 'Ensure Accuracy',
        description:
          'Eliminate manual errors with automated transaction matching, validation, and exception handling.',
        icon: 'CheckCircle2',
      },
      {
        title: 'Maintain Compliance',
        description:
          'Stay audit-ready with built-in compliance modules and comprehensive, immutable audit trails.',
        icon: 'Shield',
      },
      {
        title: 'Gain Real-Time Visibility',
        description:
          'Access real-time financial data and executive dashboards for faster, better-informed decisions.',
        icon: 'BarChart3',
      },
    ],
    capabilities: [
      'Automated ledger and journal management',
      'Accounts payable and receivable tracking',
      'Bank reconciliation automation',
      'GST and compliance-ready modules',
      'Budget monitoring and expense controls',
      'Real-time financial dashboards',
      'Multi-entity consolidation and reporting',
      'Immutable audit trail and compliance reports',
    ],
    quote: {
      text: 'The ERP system brought discipline to our operations without restricting creativity. It has strengthened execution speed and financial clarity significantly.',
      author: 'Director, Graphic Eye',
      role: 'Creative & Media Agency',
    },
  },
}
